import { useState } from 'react';
import { useNotification } from '@hooks/useNotification';
import { useLanguage } from '@contexts/LanguageContext';

/**
 * Centralized error management hook for forms
 * Provides consistent error handling across all forms
 */
export const useFormErrorHandler = () => {
    const [error, setError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const notify = useNotification();
    const { t } = useLanguage();

    /**
     * Parse API error response into user-friendly messages
     * @param {Error} err - The error object from API response
     * @returns {Object} - Parsed error information
     */
    const parseApiError = (err) => {
        console.error('API Error:', err);

        // Default error message
        let message = t('common.error_occurred') || 'An error occurred';
        let details = [];
        let fields = {};

        if (!err?.response) {
            // Network or other non-HTTP errors
            message = t('common.networkError') || 'Network error. Please check your connection.';
            return { message, details, fields };
        }

        const { status, data } = err.response;

        switch (status) {
            case 400:
                message = t('common.badRequest') || 'Invalid request data';
                break;
            case 401:
                message = t('common.unauthorized') || 'Authentication required';
                break;
            case 403:
                message = t('common.forbidden') || 'Access denied';
                break;
            case 404:
                message = t('common.notFound') || 'Resource not found';
                break;
            case 409:
                message = t('common.conflict') || 'Conflict with existing data';
                break;
            case 413:
                message = t('common.payloadTooLarge') || 'File or data too large';
                break;
            case 422:
                // Validation errors - most common for forms
                message = t('common.validationError') || 'Please check your input';
                break;
            case 429:
                message = t('common.tooManyRequests') || 'Too many requests. Please try again later.';
                break;
            case 500:
                message = t('common.serverError') || 'Server error. Please try again later.';
                break;
            case 503:
                message = t('common.serviceUnavailable') || 'Service temporarily unavailable';
                break;
            default:
                message = t('common.unexpectedError') || 'Unexpected error occurred';
        }

        // Parse error details from response
        if (data?.detail) {
            if (typeof data.detail === 'string') {
                // Simple string error
                message = data.detail;
            } else if (Array.isArray(data.detail)) {
                // FastAPI validation errors format
                details = data.detail.map(detail => {
                    let field = '';
                    let msg = detail.msg || '';

                    // Extract field name from location
                    if (detail.loc && detail.loc.length > 0) {
                        const relevantLoc = detail.loc.filter(loc => loc !== 'body');
                        if (relevantLoc.length > 0) {
                            field = relevantLoc[relevantLoc.length - 1];
                        }
                    }

                    // Store field-specific errors
                    if (field) {
                        fields[field] = msg;
                        return `${field}: ${msg}`;
                    }
                    return msg;
                });

                // If we have field-specific errors, update the message
                if (Object.keys(fields).length > 0) {
                    message = t('common.validationError') || 'Please check the following fields';
                }
            } else if (typeof data.detail === 'object') {
                // Object format errors
                Object.entries(data.detail).forEach(([field, msg]) => {
                    fields[field] = Array.isArray(msg) ? msg.join(', ') : msg;
                    details.push(`${field}: ${fields[field]}`);
                });
            }
        }

        // Fallback to error message if no detail
        if (!data?.detail && data?.message) {
            message = data.message;
        }

        return { message, details, fields };
    };

    /**
     * Handle API errors with consistent behavior
     * @param {Error} err - The error object
     * @param {Object} options - Configuration options
     * @param {boolean} options.showToast - Whether to show toast notification (default: true)
     * @param {boolean} options.setFormError - Whether to set form error state (default: true)
     * @param {boolean} options.rethrow - Whether to rethrow the error (default: false)
     */
    const handleError = (err, options = {}) => {
        const {
            showToast = true,
            setFormError = true,
            rethrow = false
        } = options;

        const { message, details, fields } = parseApiError(err);

        // Set field-specific errors
        if (Object.keys(fields).length > 0) {
            setFieldErrors(fields);
        }

        // Set general form error
        if (setFormError) {
            if (details.length > 0) {
                setError(details.join(', '));
            } else {
                setError(message);
            }
        }

        // Show toast notification
        if (showToast) {
            if (details.length > 0 && details.length <= 3) {
                // Show individual field errors if not too many
                details.forEach(detail => notify.error(detail));
            } else {
                // Show general error message
                notify.error(message);
            }
        }

        // Rethrow if requested (for backward compatibility)
        if (rethrow) {
            throw err;
        }
    };

    /**
     * Clear all errors
     */
    const clearErrors = () => {
        setError(null);
        setFieldErrors({});
    };

    /**
     * Clear specific field error
     * @param {string} fieldName - Name of the field to clear
     */
    const clearFieldError = (fieldName) => {
        setFieldErrors(prev => {
            const updated = { ...prev };
            delete updated[fieldName];
            return updated;
        });
    };

    /**
     * Set a custom error message
     * @param {string} message - Error message to display
     */
    const setCustomError = (message) => {
        setError(message);
    };

    /**
     * Set field-specific error
     * @param {string} fieldName - Name of the field
     * @param {string} message - Error message
     */
    const setFieldError = (fieldName, message) => {
        setFieldErrors(prev => ({
            ...prev,
            [fieldName]: message
        }));
    };

    /**
     * Get error for specific field
     * @param {string} fieldName - Name of the field
     * @returns {string|null} - Error message or null
     */
    const getFieldError = (fieldName) => {
        return fieldErrors[fieldName] || null;
    };

    /**
     * Check if there are any errors
     * @returns {boolean} - True if there are errors
     */
    const hasErrors = () => {
        return !!error || Object.keys(fieldErrors).length > 0;
    };

    return {
        // Error state
        error,
        fieldErrors,

        // Error handling methods
        handleError,
        parseApiError,

        // Error management
        clearErrors,
        clearFieldError,
        setCustomError,
        setFieldError,
        getFieldError,
        hasErrors
    };
};

export default useFormErrorHandler;
